import { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const PatientFormDialog = ({ open, onClose, onSuccess, patient = null }) => {
  const [formData, setFormData] = useState(patient || {
    identite: {
      nom: '',
      prenom: '',
      date_naissance: '',
      sexe: 'M',
      telephone: '',
      adresse: ''
    },
    allergies: [],
    antecedents: [],
    assurance: '',
    numero_assurance: ''
  });
  const [allergiesText, setAllergiesText] = useState(patient?.allergies?.join(', ') || '');
  const [antecedentsText, setAntecedentsText] = useState(patient?.antecedents?.join(', ') || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const dataToSend = {
        ...formData,
        allergies: allergiesText.split(',').map(a => a.trim()).filter(a => a),
        antecedents: antecedentsText.split(',').map(a => a.trim()).filter(a => a)
      };

      if (patient) {
        await axios.put(`${API}/patients/${patient.id}`, dataToSend);
      } else {
        await axios.post(`${API}/patients`, dataToSend);
      }

      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="patient-form-dialog">
        <DialogHeader>
          <DialogTitle>{patient ? 'Modifier le patient' : 'Nouveau patient'}</DialogTitle>
          <DialogDescription>
            Remplissez les informations du patient
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="nom">Nom *</Label>
              <Input
                id="nom"
                data-testid="patient-nom-input"
                value={formData.identite.nom}
                onChange={(e) => setFormData({
                  ...formData,
                  identite: { ...formData.identite, nom: e.target.value }
                })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="prenom">Prénom *</Label>
              <Input
                id="prenom"
                data-testid="patient-prenom-input"
                value={formData.identite.prenom}
                onChange={(e) => setFormData({
                  ...formData,
                  identite: { ...formData.identite, prenom: e.target.value }
                })}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="date_naissance">Date de naissance *</Label>
              <Input
                id="date_naissance"
                type="date"
                data-testid="patient-dob-input"
                value={formData.identite.date_naissance}
                onChange={(e) => setFormData({
                  ...formData,
                  identite: { ...formData.identite, date_naissance: e.target.value }
                })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sexe">Sexe *</Label>
              <select
                id="sexe"
                data-testid="patient-sexe-select"
                value={formData.identite.sexe}
                onChange={(e) => setFormData({
                  ...formData,
                  identite: { ...formData.identite, sexe: e.target.value }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="M">Masculin</option>
                <option value="F">Féminin</option>
              </select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="telephone">Téléphone</Label>
            <Input
              id="telephone"
              data-testid="patient-tel-input"
              value={formData.identite.telephone}
              onChange={(e) => setFormData({
                ...formData,
                identite: { ...formData.identite, telephone: e.target.value }
              })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="adresse">Adresse</Label>
            <Textarea
              id="adresse"
              data-testid="patient-adresse-input"
              value={formData.identite.adresse}
              onChange={(e) => setFormData({
                ...formData,
                identite: { ...formData.identite, adresse: e.target.value }
              })}
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="allergies">Allergies (séparées par des virgules)</Label>
            <Input
              id="allergies"
              data-testid="patient-allergies-input"
              placeholder="Ex: Pénicilline, Arachides"
              value={allergiesText}
              onChange={(e) => setAllergiesText(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="antecedents">Antécédents (séparés par des virgules)</Label>
            <Textarea
              id="antecedents"
              data-testid="patient-antecedents-input"
              placeholder="Ex: Hypertension, Diabète"
              value={antecedentsText}
              onChange={(e) => setAntecedentsText(e.target.value)}
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="assurance">Assurance</Label>
              <Input
                id="assurance"
                data-testid="patient-assurance-input"
                value={formData.assurance}
                onChange={(e) => setFormData({ ...formData, assurance: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="numero_assurance">N° Assurance</Label>
              <Input
                id="numero_assurance"
                data-testid="patient-num-assurance-input"
                value={formData.numero_assurance}
                onChange={(e) => setFormData({ ...formData, numero_assurance: e.target.value })}
              />
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
              {error}
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={loading}
              data-testid="save-patient-button"
              style={{
                background: 'linear-gradient(135deg, #0066cc 0%, #4a90e2 100%)',
                color: 'white'
              }}
            >
              {loading ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PatientFormDialog;
